import { useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { ReservedSavedAddressType } from "@mapmycustomers/shared/enum/SavedAddressType";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import useSavedAddressesRequest from "@app/component/savedLocations/useSavedAddressesRequest";
import GlobalSearchItemType from "@app/enum/GlobalSearchItemType";
import Path from "@app/enum/Path";
import { doesOrganizationSupportSavedLocations } from "@app/store/iam";
import { isGeneralMapHiddenInFieldView } from "@app/store/mapLayers/selectors";
import { areSavedAddressesLoaded, getSavedAddresses } from "@app/store/savedLocations";
import { createSavedAddress } from "@app/store/savedLocations/actions";
import { showSavedLocationCard } from "@app/store/savedLocationsUi";
import GlobalSearchItem, {
  SavedLocationGlobalSearchItem,
  SetSavedLocationGlobalSearchItem,
} from "@app/types/globalSearch/GlobalSearchItem";
import MapLocationState from "@app/types/map/MapLocationState";
import {
  SavedLocationCreateSource,
  SavedLocationEvent,
  SavedLocationSelectSurface,
  trackSavedLocationEvent,
} from "@app/util/analytic/savedLocationsAnalytics";
import {
  hasSavedAddressErrorCode,
  RESERVED_SLOT_TAKEN_CODE,
} from "@app/util/savedLocations/savedAddressErrors";
import {
  getSavedAddressList,
  mapSavedAddressList,
} from "@app/util/savedLocations/savedAddressList";

import GlobalSearchFilter from "../enum/GlobalSearchFilter";

import {
  isAddressGlobalSearchItem,
  isSavedLocationGlobalSearchItem,
  isSetSavedLocationGlobalSearchItem,
} from "./assert";
import useAddressPredictions from "./useAddressPredictions";

const useSavedLocationsSearch = ({
  filter,
  onHide,
  onSetModeTypeChange,
  query,
  setModeType,
  setQuery,
}: {
  filter: GlobalSearchFilter;
  onHide: () => void;
  onSetModeTypeChange: (type?: ReservedSavedAddressType) => void;
  query: string;
  setModeType?: ReservedSavedAddressType;
  setQuery: (query: string) => void;
}) => {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  useSavedAddressesRequest();
  const supported = useSelector(doesOrganizationSupportSavedLocations);
  const loaded = useSelector(areSavedAddressesLoaded);
  const savedAddresses = useSelector(getSavedAddresses);
  const fieldViewFocused = useSelector(isGeneralMapHiddenInFieldView);
  const creatingRef = useRef(false);
  const enabled = supported && loaded;
  // Only in Set mode does the rep type an address on purpose; other searches name customers
  const { answered: addressesAnswered, items: addressItems } = useAddressPredictions(
    query,
    enabled && !!setModeType
  );
  // A save can answer after the rep has left or changed Set mode
  const isStillSetting = useDynamicCallback(
    (type: ReservedSavedAddressType) => setModeType === type
  );

  const savedLocationItems = useMemo(() => {
    if (
      !enabled ||
      setModeType ||
      (filter !== GlobalSearchFilter.ALL && filter !== GlobalSearchFilter.SAVED_LOCATIONS)
    ) {
      return [];
    }
    return mapSavedAddressList<SavedLocationGlobalSearchItem | SetSavedLocationGlobalSearchItem>(
      getSavedAddressList(intl, savedAddresses, query),
      (savedAddress) => ({
        entity: undefined,
        entityType: undefined,
        savedAddress,
        title: "",
        type: GlobalSearchItemType.SAVED_LOCATION,
      }),
      (savedAddressType) => ({
        entity: undefined,
        entityType: undefined,
        savedAddressType,
        title: "",
        type: GlobalSearchItemType.SET_SAVED_LOCATION,
      })
    );
  }, [enabled, filter, intl, query, savedAddresses, setModeType]);

  const leaveSetMode = useCallback(() => {
    onSetModeTypeChange(undefined);
    setQuery("");
  }, [onSetModeTypeChange, setQuery]);

  const handleSelect = useCallback(
    (item: GlobalSearchItem) => {
      if (isSetSavedLocationGlobalSearchItem(item)) {
        onSetModeTypeChange(item.savedAddressType);
        setQuery("");
        return true;
      }
      if (isAddressGlobalSearchItem(item)) {
        if (setModeType && !creatingRef.current) {
          creatingRef.current = true;
          dispatch(
            createSavedAddress.request({
              callback: () => {
                creatingRef.current = false;
                if (isStillSetting(setModeType)) {
                  leaveSetMode();
                }
              },
              failureCallback: (error) => {
                creatingRef.current = false;
                // Set elsewhere meanwhile: the refetched list shows it, and retries would fail too
                if (
                  hasSavedAddressErrorCode(error, RESERVED_SLOT_TAKEN_CODE) &&
                  isStillSetting(setModeType)
                ) {
                  leaveSetMode();
                }
              },
              payload: {
                address: item.prediction.description,
                placeId: item.prediction.place_id,
                type: setModeType,
              },
              source: SavedLocationCreateSource.RESERVED_ROW,
            })
          );
        }
        return true;
      }
      if (!isSavedLocationGlobalSearchItem(item)) {
        return false;
      }
      const { id, geoPoint, type } = item.savedAddress;
      trackSavedLocationEvent(SavedLocationEvent.SELECTED, {
        kind: type,
        query_was_empty: !query.trim(),
        surface: SavedLocationSelectSurface.UNIVERSAL_SEARCH,
      });
      const state: MapLocationState = { center: geoPoint.coordinates };
      history.push({ pathname: Path.MAP, state });
      // The card mounts only with the gold pins; dispatched without them, it would open later
      if (!fieldViewFocused) {
        dispatch(showSavedLocationCard({ id }));
      }
      onHide();
      return true;
    },
    [
      dispatch,
      fieldViewFocused,
      history,
      isStillSetting,
      leaveSetMode,
      onHide,
      onSetModeTypeChange,
      query,
      setModeType,
      setQuery,
    ]
  );

  return {
    addressesAnswered,
    addressItems,
    enabled,
    handleSelect,
    leaveSetMode,
    savedLocationItems,
  };
};

export default useSavedLocationsSearch;
